import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  buildSelectedAreaSceneObjects,
  DEBUG_MANUAL_3D,
  disposeSceneObjects,
  WALL_HEIGHT,
} from '../lib/selectedAreaThreeModel';
import { loadFloorPlanTextures } from '../lib/floorPlanThreeTextures';
import {
  buildBackgroundReferenceImagePlane,
  disposeBackgroundReferenceImagePlane,
} from '../lib/floorPlanBackgroundImage3D.js';
import {
  fitCameraToBounds3D,
  getFloorPlan3DBounds,
} from '../lib/floorPlanContentBounds.js';
import { ZOOM_FACTOR_3D } from '../lib/floorPlanDesignerConfig.js';
import {
  applyClippingWithState,
  computeMaxVisibleHeightM,
  createFloorPlanClippingState,
  getMaximumWallHeightM,
  updateFloorPlanVisibleHeightPlane,
} from '../lib/floorPlanThreeClipping.js';
import {
  computeGridFootprint,
  configureFloorPlanRenderer,
  createFloorPlanGrid,
  createFloorPlanLights,
  createFloorPlanWebGLRenderer,
  disposeFloorPlanGrid,
  disposeFloorPlanLights,
  disposeFloorPlanWebGLRenderer,
  positionFloorPlanGrid,
} from '../lib/floorPlanThreeSceneSetup';
import { resolveFloorPlanRenderQuality, readDevAmbientOcclusionPreviewEnabled, writeDevAmbientOcclusionPreviewEnabled, readDevWallMaterialTestMode } from '../lib/floorPlanThreeRenderConfig.js';
import {
  createFloorPlanPostProcessing,
  disposeFloorPlanPostProcessing,
  renderFloorPlanFrame,
  resizeFloorPlanPostProcessing,
} from '../lib/floorPlanThreePostProcessing.js';
import {
  applyDevWallMaterialTest,
  logFloorPlanRendererDiagnostics,
  logWallMeshMaterial,
  logWallMeshesForId,
  summarizeWallMaterial,
} from '../lib/floorPlanThreeDiagnostics.js';
import { applyJoystickMovement } from '../lib/roomRenderCamera.js';
import { captureRoomRenderViewport } from '../lib/captureRoomRenderViewport.js';

const MIN_CAMERA_DISTANCE_3D = 0.5;
const MAX_CAMERA_DISTANCE_3D = 500;
const LOOK_SENSITIVITY = 0.003;
const MAX_LOOK_PITCH = THREE.MathUtils.degToRad(85);
const LOOK_TARGET_DISTANCE = 2;
const ROTATE_HINT_DRAG_THRESHOLD_PX = 6;

function vectorToYawPitch(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const horizontal = Math.hypot(dx, dz);
  return {
    yaw: Math.atan2(dx, dz),
    pitch: Math.atan2(dy, horizontal),
  };
}

function directionFromYawPitch(yaw, pitch) {
  const cosPitch = Math.cos(pitch);
  return new THREE.Vector3(
    Math.sin(yaw) * cosPitch,
    Math.sin(pitch),
    Math.cos(yaw) * cosPitch,
  ).normalize();
}

function syncLookStateFromCamera(sceneState) {
  const lookOnly = sceneState?.lookOnlyState;
  const camera = sceneState?.camera;
  if (!lookOnly || !camera) return;
  const direction = new THREE.Vector3();
  camera.getWorldDirection(direction);
  lookOnly.yaw = Math.atan2(direction.x, direction.z);
  lookOnly.pitch = Math.asin(THREE.MathUtils.clamp(direction.y, -1, 1));
}

function applyFirstPersonLook(sceneState) {
  const lookOnly = sceneState?.lookOnlyState;
  const camera = sceneState?.camera;
  const controls = sceneState?.controls;
  if (!lookOnly || !camera || !controls) return null;

  const positionBefore = camera.position.clone();
  const direction = directionFromYawPitch(lookOnly.yaw, lookOnly.pitch);
  const target = camera.position.clone().add(direction.clone().multiplyScalar(LOOK_TARGET_DISTANCE));

  camera.lookAt(target);
  camera.updateMatrixWorld();
  controls.target.copy(target);
  controls.update();

  if (process.env.NODE_ENV === 'development') {
    console.assert(
      camera.position.distanceTo(positionBefore) < 1e-8,
      'Look rotation must not move camera position',
    );
  }

  return direction;
}

/**
 * @param {THREE.PerspectiveCamera} camera
 * @param {import('three/examples/jsm/controls/OrbitControls.js').OrbitControls} controls
 * @param {THREE.Object3D} modelRoot
 */
function fitCameraToModel(camera, controls, modelRoot) {
  const box = getFloorPlan3DBounds({ roots: [modelRoot] });
  fitCameraToBounds3D(camera, controls, box);
}

/**
 * @param {THREE.PerspectiveCamera} camera
 * @param {import('three/examples/jsm/controls/OrbitControls.js').OrbitControls} controls
 * @param {Array<THREE.Object3D | null | undefined>} roots
 */
function fitCameraToSceneContent(camera, controls, roots) {
  const box = getFloorPlan3DBounds({ roots, excludeNames: ['grid'] });
  fitCameraToBounds3D(camera, controls, box);
}

/**
 * @param {ReturnType<typeof mountThreePreview> extends { sceneState: infer S } ? S : never} sceneState
 * @param {boolean} zoomIn
 */
function applyZoom3D(sceneState, zoomIn) {
  if (!sceneState?.camera || !sceneState?.controls || sceneState.isDisposed) return;
  const { camera, controls } = sceneState;
  const direction = camera.position.clone().sub(controls.target);
  const distance = direction.length();
  if (distance <= 1e-6) return;
  const factor = zoomIn ? ZOOM_FACTOR_3D : 1 / ZOOM_FACTOR_3D;
  const nextDistance = Math.min(
    MAX_CAMERA_DISTANCE_3D,
    Math.max(MIN_CAMERA_DISTANCE_3D, distance * factor),
  );
  camera.position.copy(controls.target.clone().add(direction.normalize().multiplyScalar(nextDistance)));
  camera.updateProjectionMatrix();
  controls.update();
  renderFloorPlanFrame(sceneState);
}

/**
 * @param {HTMLDivElement} mount
 * @param {NonNullable<ReturnType<typeof buildSelectedAreaSceneObjects>>} buildResult
 * @param {boolean} manualDesigner
 * @param {boolean} showIds
 * @param {{ cameraControlMode?: 'orbit' | 'lookOnly'; onLookChange?: () => void; onCameraLookDrag?: () => void; analysisJson?: Record<string, unknown> | null; visibleHeightM?: number; onClippingStateReady?: (state: { maxWallHeight: number; maxVisibleHeight: number }) => void; renderQuality?: ReturnType<typeof resolveFloorPlanRenderQuality>; joystickInputRef?: React.MutableRefObject<{ x: number; y: number; active: boolean } | null>; roomMoveBounds?: { polygon: number[][]; metersPerPixel: number; marginM?: number } | null }} [options]
 * @param {(sceneState: object) => void} [onManualCameraReady]
 * @returns {{ sceneState: object; dispose: () => void }}
 */
function mountThreePreview(mount, buildResult, manualDesigner, showIds, options = {}, onManualCameraReady) {
  const width = mount.clientWidth || 640;
  const height = mount.clientHeight || 420;
  const renderQuality = options.renderQuality ?? resolveFloorPlanRenderQuality();
  let isDisposed = false;

  const scene = new THREE.Scene();
  scene.background = null;

  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
  const sceneRadius = buildResult.sceneRadius;

  const renderer = createFloorPlanWebGLRenderer(mount);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, renderQuality.pixelRatioMax));
  renderer.setSize(width, height);
  configureFloorPlanRenderer(renderer);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  const lookOnlyMode = options.cameraControlMode === 'lookOnly';
  if (lookOnlyMode) {
    controls.enabled = true;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.enableRotate = false;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;
    controls.minAzimuthAngle = -Infinity;
    controls.maxAzimuthAngle = Infinity;
    controls.minDistance = MIN_CAMERA_DISTANCE_3D;
    controls.maxDistance = MAX_CAMERA_DISTANCE_3D;
  }

  const lights = createFloorPlanLights(scene, sceneRadius, renderQuality.lighting);
  const maxWallHeightM = getMaximumWallHeightM(buildResult, options.analysisJson);
  const visibleHeightM = Number.isFinite(options.visibleHeightM)
    ? options.visibleHeightM
    : computeMaxVisibleHeightM(maxWallHeightM);
  const clippingState = createFloorPlanClippingState({ visibleHeightM, maxWallHeightM });
  /** @type {THREE.Material[]} */
  const clippedMaterials = [];

  const modelGroup = new THREE.Group();
  const disposableMeshes = [];
  if (buildResult.floor) {
    if (buildResult.floor.userData) {
      buildResult.floor.userData.skipVisibleHeightClip = true;
    }
    modelGroup.add(buildResult.floor);
    disposableMeshes.push(buildResult.floor);
  }
  if (buildResult.ceilings) {
    clippedMaterials.push(...applyClippingWithState(buildResult.ceilings, clippingState));
    modelGroup.add(buildResult.ceilings);
    disposableMeshes.push(buildResult.ceilings);
  }
  buildResult.walls.forEach((mesh) => {
    clippedMaterials.push(...applyClippingWithState(mesh, clippingState));
    modelGroup.add(mesh);
    disposableMeshes.push(mesh);
  });
  scene.add(modelGroup);

  if (process.env.NODE_ENV === 'development') {
    logFloorPlanRendererDiagnostics(renderer, scene);
    const firstWallMesh = buildResult.walls?.[0];
    firstWallMesh?.traverse?.((child) => {
      if (child instanceof THREE.Mesh && child.userData?.wallId && !child.userData?.isWallEdgeOverlay) {
        logWallMeshMaterial(child);
        if (child.userData.wallId) {
          logWallMeshesForId(modelGroup, child.userData.wallId);
        }
      }
    });
    if (buildResult.floor instanceof THREE.Mesh) {
      console.debug('[FloorPlan3D] floor material', summarizeWallMaterial(buildResult.floor.material));
    }
    applyDevWallMaterialTest(modelGroup, readDevWallMaterialTestMode());
  }

  const objectsGroup = new THREE.Group();
  objectsGroup.name = 'objectsGroup';
  (buildResult.objects || []).forEach((object3D) => {
    clippedMaterials.push(...applyClippingWithState(object3D, clippingState));
    objectsGroup.add(object3D);
    disposableMeshes.push(object3D);
  });
  scene.add(objectsGroup);

  options.onClippingStateReady?.({
    maxWallHeight: clippingState.maxWallHeight,
    maxVisibleHeight: clippingState.maxVisibleHeight,
  });

  /** @type {THREE.Mesh | null} */
  let backgroundPlane = null;
  const backgroundRef = options.analysisJson?.backgroundReferenceImage;

  const { footprint, centerX, centerZ } = computeGridFootprint(modelGroup);
  const grid = createFloorPlanGrid(footprint);
  grid.name = 'grid';
  positionFloorPlanGrid(grid, centerX, centerZ);
  scene.add(grid);

  const markerGroup = new THREE.Group();
  markerGroup.visible = showIds;
  const debugGroup = new THREE.Group();
  debugGroup.visible = DEBUG_MANUAL_3D;
  buildResult.markers.forEach((marker) => {
    if (marker.userData?.debugRole) {
      debugGroup.add(marker);
    } else {
      markerGroup.add(marker);
    }
    disposableMeshes.push(marker);
  });
  scene.add(markerGroup);
  if (debugGroup.children.length > 0) {
    scene.add(debugGroup);
  }

  const postProcessing = createFloorPlanPostProcessing(
    renderer,
    scene,
    camera,
    width,
    height,
    renderQuality.ambientOcclusion,
  );

  /** @type {ReturnType<typeof mountThreePreview>['sceneState']} */
  const sceneState = {
    camera,
    controls,
    renderer,
    composer: postProcessing?.composer ?? null,
    postProcessing,
    scene,
    sceneRadius,
    modelGroup,
    objectsGroup,
    backgroundPlane: () => backgroundPlane,
    grid,
    lights,
    isManualDesigner: manualDesigner,
    markerGroup,
    debugGroup,
    disposableMeshes,
    clippingState,
    clipPlane: clippingState.plane,
    clippedMaterials,
    fitContent: null,
    isDisposed: false,
    lookOnlyState: lookOnlyMode
      ? {
          fixedPosition: null,
          yaw: 0,
          pitch: 0,
          dragging: false,
          pointerId: null,
          lastX: 0,
          lastY: 0,
        }
      : null,
  };

  sceneState.fitContent = () => {
    if (isDisposed) return;
    fitCameraToSceneContent(camera, controls, [modelGroup, objectsGroup, backgroundPlane]);
    renderFloorPlanFrame(sceneState);
  };
  sceneState.fit3DToScreen = () => sceneState.fitContent?.();
  sceneState.zoom3DIn = () => applyZoom3D(sceneState, true);
  sceneState.zoom3DOut = () => applyZoom3D(sceneState, false);

  if (manualDesigner) {
    if (!lookOnlyMode) {
      sceneState.fitContent();
    }
    onManualCameraReady?.(sceneState);
  } else {
    camera.position.set(sceneRadius * 0.8, sceneRadius * 0.7, sceneRadius * 0.8);
    controls.target.set(0, WALL_HEIGHT / 2, 0);
    controls.update();
  }

  if (backgroundRef?.visible && backgroundRef?.source) {
    buildBackgroundReferenceImagePlane({
      background: backgroundRef,
      center: buildResult.center || { x: 0, y: 0 },
      metersPerPixel: buildResult.metersPerPixel || 0.01,
    }).then((mesh) => {
      if (isDisposed || !mesh) return;
      backgroundPlane = mesh;
      scene.add(mesh);
      if (manualDesigner && !lookOnlyMode) {
        sceneState.fitContent?.();
      } else {
        renderFloorPlanFrame(sceneState);
      }
    }).catch(() => {});
  }

  const domElement = renderer.domElement;
  let rotateHintStart = null;
  let rotateHintTriggered = false;
  const handleLookPointerDown = (event) => {
    const lookOnly = sceneState.lookOnlyState;
    if (!lookOnly || event.button !== 0) return;
    event.preventDefault();
    lookOnly.dragging = true;
    lookOnly.pointerId = event.pointerId;
    lookOnly.lastX = event.clientX;
    lookOnly.lastY = event.clientY;
    rotateHintStart = {
      x: event.clientX,
      y: event.clientY,
    };
    domElement.setPointerCapture?.(event.pointerId);
  };
  const handleLookPointerMove = (event) => {
    const lookOnly = sceneState.lookOnlyState;
    if (!lookOnly?.dragging || lookOnly.pointerId !== event.pointerId) return;
    event.preventDefault();

    const deltaX = event.clientX - lookOnly.lastX;
    const deltaY = event.clientY - lookOnly.lastY;
    if (rotateHintStart && !rotateHintTriggered) {
      const distance = Math.hypot(
        event.clientX - rotateHintStart.x,
        event.clientY - rotateHintStart.y,
      );
      if (distance >= ROTATE_HINT_DRAG_THRESHOLD_PX) {
        rotateHintTriggered = true;
        options.onCameraLookDrag?.();
      }
    }
    lookOnly.lastX = event.clientX;
    lookOnly.lastY = event.clientY;
    lookOnly.yaw -= deltaX * LOOK_SENSITIVITY;
    lookOnly.pitch = THREE.MathUtils.clamp(
      lookOnly.pitch - deltaY * LOOK_SENSITIVITY,
      -MAX_LOOK_PITCH,
      MAX_LOOK_PITCH,
    );

    applyFirstPersonLook(sceneState);
    renderFloorPlanFrame(sceneState);
    options.onLookChange?.();
  };
  const endLookDrag = (event) => {
    const lookOnly = sceneState.lookOnlyState;
    if (!lookOnly || lookOnly.pointerId !== event.pointerId) return;
    lookOnly.dragging = false;
    lookOnly.pointerId = null;
    rotateHintStart = null;
    domElement.releasePointerCapture?.(event.pointerId);
    options.onLookChange?.();
  };
  const handleWheel = (event) => {
    if (!sceneState.lookOnlyState) return;
    event.preventDefault();
  };

  if (lookOnlyMode) {
    domElement.addEventListener('pointerdown', handleLookPointerDown);
    domElement.addEventListener('pointermove', handleLookPointerMove);
    domElement.addEventListener('pointerup', endLookDrag);
    domElement.addEventListener('pointercancel', endLookDrag);
    domElement.addEventListener('wheel', handleWheel, { passive: false });
  }

  let frameId = 0;
  let lastFrameTime = performance.now();
  const animate = (now) => {
    if (isDisposed) return;
    frameId = window.requestAnimationFrame(animate);
    const deltaTime = Math.min(0.05, Math.max(0, (now - lastFrameTime) / 1000));
    lastFrameTime = now;

    const joystickInput = options.joystickInputRef?.current;
    const roomMoveBounds = options.roomMoveBounds;
    if (lookOnlyMode && joystickInput?.active && roomMoveBounds) {
      const moved = applyJoystickMovement(sceneState, joystickInput, deltaTime, roomMoveBounds);
      if (moved) {
        options.onLookChange?.();
      }
    }

    controls.update();
    renderFloorPlanFrame(sceneState);
  };
  animate(performance.now());

  const handleResize = () => {
    if (isDisposed) return;
    const nextWidth = mount.clientWidth || 640;
    const nextHeight = mount.clientHeight || 420;
    renderer.setSize(nextWidth, nextHeight, false);
    camera.aspect = nextWidth / nextHeight;
    camera.updateProjectionMatrix();
    resizeFloorPlanPostProcessing(sceneState, nextWidth, nextHeight);
    renderFloorPlanFrame(sceneState);
  };
  sceneState.resize = handleResize;
  window.addEventListener('resize', handleResize);
  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handleResize) : null;
  resizeObserver?.observe(mount);

  const dispose = () => {
    if (isDisposed) return;
    isDisposed = true;
    sceneState.isDisposed = true;
    window.cancelAnimationFrame(frameId);
    window.removeEventListener('resize', handleResize);
    resizeObserver?.disconnect();
    if (lookOnlyMode) {
      domElement.removeEventListener('pointerdown', handleLookPointerDown);
      domElement.removeEventListener('pointermove', handleLookPointerMove);
      domElement.removeEventListener('pointerup', endLookDrag);
      domElement.removeEventListener('pointercancel', endLookDrag);
      domElement.removeEventListener('wheel', handleWheel);
    }
    if (backgroundPlane) {
      disposeBackgroundReferenceImagePlane(backgroundPlane);
      backgroundPlane = null;
    }
    controls.dispose();
    disposeFloorPlanPostProcessing(postProcessing);
    if (sceneState.grid) {
      disposeFloorPlanGrid(sceneState.grid);
    }
    if (sceneState.lights) {
      disposeFloorPlanLights(sceneState.lights);
    }
    disposeSceneObjects(disposableMeshes, buildResult.materials || []);
    disposeFloorPlanWebGLRenderer(renderer, mount);
  };

  return { sceneState, dispose };
}

function getLookOnlyCameraState(sceneState, cameraFov) {
  const camera = sceneState?.camera;
  const controls = sceneState?.controls;
  if (!camera || !controls) return null;
  const { yaw, pitch } = vectorToYawPitch(camera.position, controls.target);
  return {
    position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
    target: { x: controls.target.x, y: controls.target.y, z: controls.target.z },
    lookDirection: { yaw, pitch },
    fov: Number.isFinite(cameraFov) ? cameraFov : camera.fov,
  };
}

function applyCameraPreset(sceneState, cameraPreset, cameraFov, options = {}) {
  if (!sceneState?.camera || !sceneState?.controls || sceneState.isDisposed) return;
  const { resetLookOffsets = true, updateBaseLook = true } = options;
  const { camera, controls } = sceneState;
  if (Number.isFinite(cameraFov)) {
    camera.fov = cameraFov;
    camera.updateProjectionMatrix();
  }
  if (cameraPreset?.position && cameraPreset?.target) {
    const { position, target } = cameraPreset;
    camera.position.set(position.x, position.y, position.z);
    controls.target.set(target.x, target.y, target.z);
    camera.lookAt(controls.target);
    if (sceneState.lookOnlyState) {
      if (!sceneState.lookOnlyState.fixedPosition) {
        sceneState.lookOnlyState.fixedPosition = new THREE.Vector3();
      }
      sceneState.lookOnlyState.fixedPosition.copy(camera.position);
      syncLookStateFromCamera(sceneState);
    }
    controls.update();
    if (process.env.NODE_ENV === 'development') {
      console.log('[camera-init] apply corner preset', {
        timestamp: performance.now(),
        cameraPosition: camera.position.toArray(),
        target: controls.target.toArray(),
        polarAngle: controls.getPolarAngle?.(),
        azimuthAngle: controls.getAzimuthalAngle?.(),
        resetLookOffsets,
        updateBaseLook,
      });
    }
  }
  renderFloorPlanFrame(sceneState);
}

/**
 * @param {{
 *   analysisJson: Record<string, unknown> | null;
 *   imageWidth: number;
 *   imageHeight: number;
 *   cameraPreset?: { position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number } } | null;
 *   cameraFov?: number;
 *   compact?: boolean;
 *   cameraControlMode?: 'orbit' | 'lookOnly';
 *   hideDevControls?: boolean;
 *   onCameraStateChange?: (state: {
 *     position: { x: number; y: number; z: number };
 *     target: { x: number; y: number; z: number };
 *     lookDirection: { yaw: number; pitch: number };
 *     fov: number;
 *   }) => void;
 *   onCameraLookDrag?: () => void;
 *   showVerticalWallEdges?: boolean;
 *   showHorizontalWallEdges?: boolean;
 *   visibleHeightM?: number;
 *   onRegisterFit?: (fit: (() => void) | null) => void;
 *   onRegisterViewportActions?: (actions: {
 *     fit3DToScreen?: () => void;
 *     zoom3DIn?: () => void;
 *     zoom3DOut?: () => void;
 *     resize?: () => void;
 *   } | null) => void;
 *   onRegisterCaptureActions?: (actions: {
 *     captureViewportRegion: (
 *       captureRect: { width: number; height: number; left: number; top: number },
 *       outputWidth: number,
 *       outputHeight: number,
 *     ) => Promise<Blob>;
 *   } | null) => void;
 *   joystickInputRef?: React.MutableRefObject<{ x: number; y: number; active: boolean } | null>;
 *   roomMoveBounds?: { polygon: number[][]; metersPerPixel: number; marginM?: number } | null;
 *   onClippingStateReady?: (state: { maxWallHeight: number; maxVisibleHeight: number }) => void;
 * }} props
 */
function SelectedAreaThreePreview({
  analysisJson,
  imageWidth,
  imageHeight,
  cameraPreset = null,
  cameraFov,
  compact = false,
  hideDevControls = false,
  cameraControlMode = 'orbit',
  onCameraStateChange,
  onCameraLookDrag,
  showVerticalWallEdges = false,
  showHorizontalWallEdges = false,
  visibleHeightM = computeMaxVisibleHeightM(WALL_HEIGHT),
  onRegisterFit,
  onRegisterViewportActions,
  onRegisterCaptureActions,
  joystickInputRef = null,
  roomMoveBounds = null,
  onClippingStateReady,
}) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const disposePreviewRef = useRef(null);
  const prevPresetIdRef = useRef(null);
  const cameraPresetRef = useRef(cameraPreset);
  const onCameraStateChangeRef = useRef(onCameraStateChange);
  const onCameraLookDragRef = useRef(onCameraLookDrag);
  const cameraFovRef = useRef(cameraFov);
  const joystickInputRefStable = useRef(joystickInputRef);
  const roomMoveBoundsRef = useRef(roomMoveBounds);
  const [showIds, setShowIds] = useState(false);
  const [aoPreviewEnabled, setAoPreviewEnabled] = useState(readDevAmbientOcclusionPreviewEnabled);
  const [buildError, setBuildError] = useState('');
  const [sceneWarnings, setSceneWarnings] = useState([]);
  const [cameraReady, setCameraReady] = useState(false);
  const [textureBundle, setTextureBundle] = useState(null);

  useEffect(() => {
    onCameraStateChangeRef.current = onCameraStateChange;
  }, [onCameraStateChange]);

  useEffect(() => {
    onCameraLookDragRef.current = onCameraLookDrag;
  }, [onCameraLookDrag]);

  useEffect(() => {
    cameraFovRef.current = cameraFov;
  }, [cameraFov]);

  useEffect(() => {
    cameraPresetRef.current = cameraPreset;
  }, [cameraPreset]);

  useEffect(() => {
    joystickInputRefStable.current = joystickInputRef;
  }, [joystickInputRef]);

  useEffect(() => {
    roomMoveBoundsRef.current = roomMoveBounds;
  }, [roomMoveBounds]);

  const emitCameraStateChange = useCallback(() => {
    const state = getLookOnlyCameraState(sceneRef.current, cameraFovRef.current);
    if (state) onCameraStateChangeRef.current?.(state);
  }, []);

  const emitCameraLookDrag = useCallback(() => {
    onCameraLookDragRef.current?.();
  }, []);

  const mountOptions = useMemo(
    () => ({
      cameraControlMode,
      onLookChange: emitCameraStateChange,
      onCameraLookDrag: emitCameraLookDrag,
      visibleHeightM,
      onClippingStateReady,
      renderQuality: resolveFloorPlanRenderQuality(
        process.env.NODE_ENV === 'development'
          ? { ambientOcclusion: { enabled: aoPreviewEnabled } }
          : {},
      ),
      get joystickInputRef() {
        return joystickInputRefStable.current;
      },
      get roomMoveBounds() {
        return roomMoveBoundsRef.current;
      },
    }),
    [cameraControlMode, emitCameraStateChange, emitCameraLookDrag, visibleHeightM, onClippingStateReady, aoPreviewEnabled],
  );

  useEffect(() => {
    let cancelled = false;
    loadFloorPlanTextures().then((bundle) => {
      if (!cancelled) setTextureBundle(bundle);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const buildResult = useMemo(() => {
    if (!analysisJson) return null;
    try {
      return buildSelectedAreaSceneObjects(analysisJson, imageWidth, imageHeight, {
        textures: textureBundle,
        showVerticalWallEdges,
        showHorizontalWallEdges,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to build 3D model.';
      return { error: message };
    }
  }, [analysisJson, imageWidth, imageHeight, textureBundle, showVerticalWallEdges, showHorizontalWallEdges]);

  const isManualDesigner = analysisJson?.source === 'manualFloorPlanDesigner';

  const resetCamera = useCallback(() => {
    const sceneState = sceneRef.current;
    if (!sceneState) return;
    const { fitContent, camera, controls, modelGroup, isManualDesigner: manual, sceneRadius } = sceneState;
    if (manual && typeof fitContent === 'function') {
      fitContent();
      return;
    }
    if (manual && modelGroup) {
      fitCameraToModel(camera, controls, modelGroup);
      renderFloorPlanFrame(sceneState);
      return;
    }
    camera.position.set(sceneRadius * 0.8, sceneRadius * 0.7, sceneRadius * 0.8);
    controls.target.set(0, WALL_HEIGHT / 2, 0);
    controls.update();
  }, []);

  useEffect(() => {
    const sceneState = sceneRef.current;
    if (sceneState?.isDisposed) return;
    const clipPlane = sceneState?.clippingState?.plane ?? sceneState?.clipPlane;
    if (!clipPlane) return;
    updateFloorPlanVisibleHeightPlane(clipPlane, visibleHeightM);
    renderFloorPlanFrame(sceneState);
  }, [visibleHeightM]);

  useEffect(() => {
    onRegisterFit?.(sceneRef.current?.fitContent ?? null);
    return () => onRegisterFit?.(null);
  }, [onRegisterFit, buildResult, cameraReady]);

  useEffect(() => {
    const sceneState = sceneRef.current;
    if (!sceneState || sceneState.isDisposed) {
      onRegisterViewportActions?.(null);
      return undefined;
    }
    onRegisterViewportActions?.({
      fit3DToScreen: () => sceneState.fit3DToScreen?.(),
      zoom3DIn: () => sceneState.zoom3DIn?.(),
      zoom3DOut: () => sceneState.zoom3DOut?.(),
      resize: () => sceneState.resize?.(),
    });
    return () => onRegisterViewportActions?.(null);
  }, [onRegisterViewportActions, buildResult, cameraReady]);

  useEffect(() => {
    const sceneState = sceneRef.current;
    if (!sceneState || sceneState.isDisposed) {
      onRegisterCaptureActions?.(null);
      return undefined;
    }
    onRegisterCaptureActions?.({
      captureViewportRegion: (captureRect, outputWidth, outputHeight) =>
        captureRoomRenderViewport(sceneState, captureRect, outputWidth, outputHeight),
    });
    return () => onRegisterCaptureActions?.(null);
  }, [onRegisterCaptureActions, buildResult, cameraReady]);

  useLayoutEffect(() => {
    if (!isManualDesigner) return undefined;

    if (!analysisJson) {
      setBuildError('');
      setSceneWarnings([]);
      return undefined;
    }

    if (!buildResult || 'error' in buildResult) {
      setBuildError(buildResult?.error || 'Failed to build 3D model.');
      setSceneWarnings([]);
      return undefined;
    }

    const mount = mountRef.current;
    if (!mount) return undefined;

    setBuildError('');
    setSceneWarnings(buildResult.warnings);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[Manual 3D] analysisJson', analysisJson);
      console.debug('[Manual 3D] walls count', analysisJson?.walls?.length ?? 0);
      console.debug('[Manual 3D] rooms count', analysisJson?.rooms?.length ?? 0);
      console.debug('[Manual 3D] meshes count', buildResult.walls?.length ?? 0);
    }

    setCameraReady(false);

    disposePreviewRef.current?.();
    disposePreviewRef.current = null;

    const { sceneState, dispose } = mountThreePreview(
      mount,
      buildResult,
      true,
      showIds,
      {
        cameraControlMode: mountOptions.cameraControlMode,
        onLookChange: mountOptions.onLookChange,
        onCameraLookDrag: mountOptions.onCameraLookDrag,
        analysisJson,
        visibleHeightM: mountOptions.visibleHeightM,
        onClippingStateReady: mountOptions.onClippingStateReady,
        renderQuality: mountOptions.renderQuality,
        get joystickInputRef() {
          return joystickInputRefStable.current;
        },
        get roomMoveBounds() {
          return roomMoveBoundsRef.current;
        },
      },
      (mountedSceneState) => {
        const preset = cameraPresetRef.current;
        if (preset) {
          applyCameraPreset(mountedSceneState, preset, cameraFovRef.current, {
            resetLookOffsets: true,
            updateBaseLook: true,
          });
        }
        if (process.env.NODE_ENV === 'development') {
          console.debug('[Manual 3D] cameraReady', true);
        }
        setCameraReady(true);
      },
    );
    sceneRef.current = sceneState;
    disposePreviewRef.current = dispose;
    onRegisterFit?.(sceneState.fitContent ?? null);

    return () => {
      dispose();
      disposePreviewRef.current = null;
      sceneRef.current = null;
      onRegisterFit?.(null);
      setCameraReady(false);
    };
    // showIds is synced in a separate effect to avoid rebuilding the whole scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisJson, buildResult, imageWidth, imageHeight, isManualDesigner, cameraControlMode, aoPreviewEnabled]);

  useEffect(() => {
    if (isManualDesigner) return undefined;

    if (!analysisJson) {
      setBuildError('');
      setSceneWarnings([]);
      return undefined;
    }

    if (!buildResult || 'error' in buildResult) {
      setBuildError(buildResult?.error || 'Failed to build 3D model.');
      setSceneWarnings([]);
      return undefined;
    }

    const mount = mountRef.current;
    if (!mount) return undefined;

    setBuildError('');
    setSceneWarnings(buildResult.warnings);

    disposePreviewRef.current?.();
    disposePreviewRef.current = null;

    const { sceneState, dispose } = mountThreePreview(
      mount,
      buildResult,
      false,
      showIds,
      {
        cameraControlMode: mountOptions.cameraControlMode,
        onLookChange: mountOptions.onLookChange,
        onCameraLookDrag: mountOptions.onCameraLookDrag,
        analysisJson,
        renderQuality: mountOptions.renderQuality,
        get joystickInputRef() {
          return joystickInputRefStable.current;
        },
        get roomMoveBounds() {
          return roomMoveBoundsRef.current;
        },
      },
    );
    sceneRef.current = sceneState;
    disposePreviewRef.current = dispose;

    return () => {
      dispose();
      disposePreviewRef.current = null;
      sceneRef.current = null;
    };
    // showIds is synced in a separate effect to avoid rebuilding the whole scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisJson, buildResult, imageWidth, imageHeight, isManualDesigner, cameraControlMode, aoPreviewEnabled]);

  useEffect(() => {
    if (sceneRef.current?.markerGroup) {
      sceneRef.current.markerGroup.visible = showIds;
    }
  }, [showIds]);

  useEffect(() => {
    if (!cameraReady || !sceneRef.current || !cameraPreset) return;
    const presetId = cameraPreset.id ?? null;
    const resetLookOffsets = presetId !== prevPresetIdRef.current;
    prevPresetIdRef.current = presetId;
    applyCameraPreset(sceneRef.current, cameraPreset, cameraFov, {
      resetLookOffsets,
      updateBaseLook: resetLookOffsets,
    });
    emitCameraStateChange();
  }, [cameraPreset, cameraFov, cameraReady, emitCameraStateChange]);

  const showDevControls = !compact && !hideDevControls;

  if (!analysisJson) {
    return <p className="new-2d-project-hint">Analyze selected area first.</p>;
  }

  return (
    <div className={`new-2d-project-three-wrap${hideDevControls ? ' new-2d-project-three-wrap--editor' : ''}`}>
      {showDevControls ? (
        <div className="new-2d-project-three-controls">
          <button type="button" className="btn btn-outline" onClick={resetCamera}>
            Reset 3D Camera
          </button>
          <label className="new-2d-project-three-checkbox">
            <input
              type="checkbox"
              checked={showIds}
              onChange={(event) => setShowIds(event.target.checked)}
            />
            Show wall IDs / opening IDs
          </label>
          {process.env.NODE_ENV === 'development' ? (
            <label className="new-2d-project-three-checkbox">
              <input
                type="checkbox"
                checked={aoPreviewEnabled}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setAoPreviewEnabled(enabled);
                  writeDevAmbientOcclusionPreviewEnabled(enabled);
                }}
              />
              Ambient occlusion (dev, remounts view)
            </label>
          ) : null}
        </div>
      ) : null}

      {(buildError || sceneWarnings.length > 0) && (
        <div className="new-2d-project-three-warnings" role="status">
          {buildError ? <p>{buildError}</p> : null}
          {sceneWarnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      )}

      {isManualDesigner && !cameraReady && !buildError ? (
        <p className="new-2d-project-hint">Rendering…</p>
      ) : null}

      <div
        ref={mountRef}
        className="new-2d-project-three-preview"
        aria-label="Three.js room preview"
        style={isManualDesigner ? { opacity: cameraReady ? 1 : 0 } : undefined}
      />

      {!compact && showIds && buildResult && !('error' in buildResult) ? (
        <ul className="new-2d-project-three-id-list">
          {buildResult.walls.map((mesh) => (
            <li key={mesh.userData.wallId || mesh.uuid}>{mesh.userData.wallId || 'wall'}</li>
          ))}
          {buildResult.markers.map((marker) => (
            <li key={marker.userData.openingId || marker.uuid}>
              {marker.userData.openingId || 'opening'}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default SelectedAreaThreePreview;
